// externals
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Typography } from '@interstate/components/Typography';

// libraries
import { LoadingPage } from '@makemydeal/dr-dash-components';

// types
import type { IRootProps } from '../types';

// components
import { OpenDealHeader, StyledOpenDealActivity, StyledLoadingContainer } from './Activity.style';
import { Footer } from './footer/Footer';
import { DealershipSelector } from './dealershipSelector/DealershipSelector';
import { VehiclePicker } from './vehiclePicker/VehiclePicker';
import { CustomerCard } from './customerCard/CustomerCard';
import { useOpenDeal } from '../hooks/useOpenDeal';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';

const Activity: React.FC<IRootProps> = ({ selectors }) => {
    // Helpers hooks
    const theme = useInterstateTheme();

    // Dealership Related info
    const dealer = useSelector(selectors.getSelectedDealer);
    const incompleteDealDetails = useSelector(selectors.getIncompleteDealDetails);
    const selectedDealerId = !incompleteDealDetails?.missingDealership ? `${dealer?.dealerId}` : '';
    // Vehicle Related info
    const selectedVehicle = useSelector(selectors.getSelectedVehicle);
    const isChangeVehicleEditView = useSelector(selectors.getIsChangeVehicleEditView);

    // Open Deal related info
    const { onDealershipSelect, onCustomerCardValidationChange, isDealValid, handleOpenDeal, onEditModeChange, saveDealStatus } =
        useOpenDeal(selectors);

    if (saveDealStatus == ApiRequestStatus.Loading) {
        return (
            <StyledLoadingContainer>
                <LoadingPage />
            </StyledLoadingContainer>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <StyledOpenDealActivity data-testid="dr-activities-open-deal-v2-app">
                <OpenDealHeader>
                    <Typography variant="h1">Opening Deal</Typography>
                    <Typography variant="body-md">
                        We need more information to open this deal in Manager View (All Information is required to proceed).
                    </Typography>
                </OpenDealHeader>
                <DealershipSelector onChangeDealership={onDealershipSelect} value={selectedDealerId} />
                {/* Any changes in Choose Vehicle component can be done in VehiclePicker.tsx */}
                <VehiclePicker
                    selectedVehicle={selectedVehicle}
                    editMode={isChangeVehicleEditView}
                    onEditModeChange={onEditModeChange}
                />
                <CustomerCard onCustomerCardValidationChange={onCustomerCardValidationChange}></CustomerCard>
            </StyledOpenDealActivity>
            <Footer onOpenDeal={handleOpenDeal} isValidDeal={isDealValid} />
        </ThemeProvider>
    );
};

export default Activity;
